import { useConfig, useValidation, type Rules } from '@seek/libs-shared';
import { Heading, Stack } from 'braid-design-system';
import { useEffect } from 'react';
import { DEFAULT_VARIANT_SCHEMA_VERSION } from '../../../shared/constants/launchDarklyFeatureFlags';
import {
  CURRENT_PAGE_YOUR_SALARY,
  trackContinuePressed,
  trackDisplayPage,
  trackSkipQuestionPressed,
} from '../../../shared/tracking/writeAReview/trackingEvents';
import { ReviewPageLayoutNoStepper } from '../../components/ReviewPageLayout/ReviewPageLayoutNoStepper';
import { SalaryQuestion } from '../../questions/GroupedQuestions/SalaryQuestions/SalaryQuestion';
import { RateYourSalaryQuestion } from '../../questions/SegmentedButtonQuestions/RateYourSalaryQuestion';
import { SalaryTypeQuestion } from '../../questions/SegmentedButtonQuestions/SalaryTypeQuestion';
import {
  ExperimentTwoProgress,
  type PageProps,
  type ReviewPayload,
  type SalaryRating,
  type SalaryType,
} from '../../type';

type Props = PageProps & {
  isSubmitting: boolean;
};

export const SalaryPage = ({
  review,
  reviewFormContext,
  setPage,
  setter,
  pageNumber,
  isSubmitting,
  onSubmit,
}: Props) => {
  const config = useConfig();

  useEffect(() => {
    if (reviewFormContext?.variant) {
      trackDisplayPage({
        config,
        context: reviewFormContext,
        currentPage: CURRENT_PAGE_YOUR_SALARY,
        variant: reviewFormContext.variant,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewFormContext?.variant]);

  const fields = {
    salary: review?.salary,
    salaryRating: review?.salaryRating,
    salaryType: review?.salaryType,
  };

  // Custom validation rules for experiment two - make all salary fields optional
  const rules = {
    salary: {
      validators: [],
    },
    salaryRating: {
      validators: [],
    },
    salaryType: {
      validators: [],
    },
  } as Rules<typeof fields>;

  const {
    getTone,
    getMessage,
    handleValueChangeWithValidation,
    validateWithErrors,
  } = useValidation<ReviewPayload>(fields, rules);

  const handleOnContinue = () => {
    const { isValid } = validateWithErrors();

    if (onSubmit) {
      onSubmit();
    }

    trackContinuePressed({
      config,
      context: reviewFormContext,
      validation: isValid ? 'success' : 'mandatory_questions_not_answered',
      currentPage: CURRENT_PAGE_YOUR_SALARY,
      variant: reviewFormContext?.variant ?? DEFAULT_VARIANT_SCHEMA_VERSION,
    });

    if (!isValid) {
      return;
    }
  };

  const handleOnSkip = () => {
    trackSkipQuestionPressed({
      config,
      context: reviewFormContext,
      currentPage: CURRENT_PAGE_YOUR_SALARY,
      variant: reviewFormContext?.variant ?? DEFAULT_VARIANT_SCHEMA_VERSION,
    });

    setter({
      ...review,
      salary: undefined,
      salaryRating: undefined,
      salaryType: undefined,
    });

    setPage(ExperimentTwoProgress.CareerDevelopment);
  };

  const handleSalaryTypeChange = (value: SalaryType) => {
    handleValueChangeWithValidation({
      setter,
      validatorProperty: 'salaryType',
      previousValue: review,
    })(value);

    setter((prev) => ({
      ...prev,
      salary: {
        ...prev.salary,
        minimum: undefined,
        maximum: undefined,
      },
    }));
  };

  const handleSalaryRatingChange = (value: SalaryRating) => {
    handleValueChangeWithValidation({
      setter,
      validatorProperty: 'salaryRating',
      previousValue: review,
    })(value);
  };

  return (
    <ReviewPageLayoutNoStepper
      progress={pageNumber}
      onContinue={handleOnContinue}
      onSkip={handleOnSkip}
      isContinueLoading={isSubmitting}
    >
      <Stack space="large">
        <Heading level="1">Salary</Heading>
        <Stack space="small">
          <RateYourSalaryQuestion
            onChange={handleSalaryRatingChange}
            tone={getTone('salaryRating')}
            message={getMessage('salaryRating')}
            value={review?.salaryRating}
          />
          <SalaryTypeQuestion
            onChange={handleSalaryTypeChange}
            tone={getTone('salaryType')}
            message={getMessage('salaryType')}
            value={review?.salaryType}
          />
          <SalaryQuestion
            onChange={handleValueChangeWithValidation({
              setter,
              validatorProperty: 'salary',
              previousValue: review,
            })}
            onBonusChange={() => {}}
            onSalaryChange={() => {}}
            reviewFormContext={reviewFormContext}
            salaryType={review?.salaryType}
            tone={getTone('salary')}
            message={getMessage('salary')}
            value={review?.salary}
            showBonusesQuestion={false}
            isRequired={false}
          />
        </Stack>
      </Stack>
    </ReviewPageLayoutNoStepper>
  );
};
